import React, { forwardRef } from 'react';
import MenuExpandIcon from '@atlaskit/icon/glyph/menu-expand';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import type { TriggerProps } from '@atlaskit/popup';
import { Flex } from '@atlaskit/primitives';
import { ThemedButton } from '@atlassian/jira-business-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { useGetGroupByField } from '../../../../../controllers/get-group-by-field';
import { useGroupBy } from '../../../../../controllers/group-by';
import messages from './messages';

const GROUP_ICON = <MenuExpandIcon label="" size="small" />;

type Props = Partial<Omit<TriggerProps, 'ref'>> & { isSelected: boolean; onClick: () => void };

const Trigger = forwardRef<HTMLDivElement, Props>(
	({ onClick, isSelected, ...otherProps }: Props, ref) => {
		const { formatMessage } = useIntl();
		const [groupByFieldId] = useGroupBy();

		const groupByField = useGetGroupByField();

		if (isVisualRefreshEnabled()) {
			if (fg('list_view_a11y_aria-required-children_group_by')) {
				return (
					<ThemedButton
						ref={ref}
						aria-label={formatMessage(messages.groupByActiveButtonAriaLabel, {
							label: groupByField?.name,
						})}
						testId="business-list.ui.list-view.header-actions.group-by-dropdown.trigger.button-wrapper"
						isSelected={isSelected || groupByFieldId != null}
						onClick={onClick}
						iconAfter={
							groupByFieldId == null ? <ChevronDownIcon label="" color="currentColor" /> : undefined
						}
						{...otherProps}
					>
						<Flex as="span" columnGap="space.075" alignItems="center">
							{groupByFieldId == null ? (
								formatMessage(messages.groupByButton)
							) : (
								<FormattedMessage
									{...messages.groupByActiveButton}
									values={{
										groupByFieldId: groupByField?.name,
									}}
								/>
							)}
						</Flex>
					</ThemedButton>
				);
			}
			return (
				<div ref={ref} {...otherProps}>
					<ThemedButton
						aria-label={formatMessage(messages.groupByActiveButtonAriaLabel, {
							label: groupByField?.name,
						})}
						testId="business-list.ui.list-view.header-actions.group-by-dropdown.trigger.button-wrapper"
						isSelected={isSelected || groupByFieldId != null}
						onClick={onClick}
						iconAfter={
							groupByFieldId == null ? <ChevronDownIcon label="" color="currentColor" /> : undefined
						}
					>
						<Flex as="span" columnGap="space.075" alignItems="center">
							{groupByFieldId == null ? (
								formatMessage(messages.groupByButton)
							) : (
								<FormattedMessage
									{...messages.groupByActiveButton}
									values={{
										groupByFieldId: groupByField?.name,
									}}
								/>
							)}
						</Flex>
					</ThemedButton>
				</div>
			);
		}

		if (fg('list_view_a11y_aria-required-children_group_by')) {
			return (
				<ThemedButton
					ref={ref}
					testId="business-list.ui.list-view.header-actions.group-by-dropdown.trigger.button-wrapper"
					iconBefore={GROUP_ICON}
					isSelected={isSelected}
					onClick={onClick}
					appearance="subtle"
					{...otherProps}
				>
					{groupByFieldId == null ? (
						formatMessage(messages.groupByButton)
					) : (
						<FormattedMessage
							{...messages.groupByActiveButtonOld}
							values={{
								groupByFieldId: groupByField?.name,
							}}
						/>
					)}
				</ThemedButton>
			);
		}

		return (
			<div ref={ref} {...otherProps}>
				<ThemedButton
					testId="business-list.ui.list-view.header-actions.group-by-dropdown.trigger.button-wrapper"
					iconBefore={GROUP_ICON}
					isSelected={isSelected}
					onClick={onClick}
					appearance="subtle"
				>
					{groupByFieldId == null ? (
						formatMessage(messages.groupByButton)
					) : (
						<FormattedMessage
							{...messages.groupByActiveButtonOld}
							values={{
								groupByFieldId: groupByField?.name,
							}}
						/>
					)}
				</ThemedButton>
			</div>
		);
	},
);

export default Trigger;
